import { coverBaseScale, coverScale, clampCrop, COVER_MAX_ZOOM, type CoverCrop } from "./quest-cover.ts";
import { SPOT_THUMB_OUTPUT_PX } from "./spot-thumbnail.ts";

/** Square crop frame math (same as cover, aspect 1:1). */
export function spotThumbClampCrop(c: CoverCrop): CoverCrop {
  return clampCrop(c);
}

export function spotThumbStartCrop(frameW: number, frameH: number, imageW: number, imageH: number): CoverCrop {
  const side = Math.min(frameW, frameH);
  const base = { frameW: side, frameH: side, imageW, imageH, zoom: 1, x: 0, y: 0 };
  const s = coverScale(base);
  return spotThumbClampCrop({ ...base, x: (side - imageW * s) / 2, y: (side - imageH * s) / 2 });
}

export function spotThumbPanCrop(c: CoverCrop, dx: number, dy: number): CoverCrop {
  return spotThumbClampCrop({ ...c, x: c.x + dx, y: c.y + dy });
}

export function spotThumbZoomCrop(c: CoverCrop, zoom: number, fx = c.frameW / 2, fy = c.frameH / 2): CoverCrop {
  const before = coverScale(c);
  const z = Math.min(COVER_MAX_ZOOM, Math.max(1, zoom));
  const after = coverBaseScale(c) * z;
  const px = (fx - c.x) / before;
  const py = (fy - c.y) / before;
  return spotThumbClampCrop({ ...c, zoom: z, x: fx - px * after, y: fy - py * after });
}

/** Photo region in source px and fixed square output size. */
export function spotThumbCropOutput(c: CoverCrop): {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
  width: number;
  height: number;
} {
  const s = coverScale(c);
  const sw = Math.min(c.imageW, c.frameW / s);
  const sh = Math.min(c.imageH, c.frameH / s);
  const side = Math.min(sw, sh);
  const sx = Math.min(c.imageW - side, Math.max(0, -c.x / s + (sw - side) / 2));
  const sy = Math.min(c.imageH - side, Math.max(0, -c.y / s + (sh - side) / 2));
  return { sx, sy, sw: side, sh: side, width: SPOT_THUMB_OUTPUT_PX, height: SPOT_THUMB_OUTPUT_PX };
}
